/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-22 14:05:30
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { withI18n } from '@shopify/react-i18n';
import { buildUpdateAppPermissionURL } from 'src/helpers';
import {
  Button,
  Card,
  Modal,
  TextContainer,
  TextStyle,
  Link,
  SkeletonBodyText
} from '@shopify/polaris';
import {
  SYNC_TYPE_FLOW_B2B,
  SYNC_TYPE_FLOW_MANUAL_SYNC,
  SYNC_TYPE_FLOW_BEST_SELLING,
  getSyncContent
} from 'src/constants/sync';
import Select from 'src/components/views/common/normal-select/Select';
import { connect } from 'react-redux';
import * as syncActions from 'actions/sync-actions';
import * as checklistActions from 'actions/dashboard/checklist-actions';
import { URL } from 'src/constants/links';
import { SYNC_FREQUENCY_NAME } from 'src/constants/subscription';
import { showChatbox } from 'src/integrations/chatbox';

type Props = {
  manualSyncLoading: boolean,
  error: any,
  lastSyncedDoneAt: number,
  tenantId: string,
  getSyncStatus: (tenantId: string) => void,
  resetSyncStatus: () => void,
  syncProcessLoading: any,
  installSyncStatus: string,
  installSyncProgress: number,
  flow: any,
  getSyncProcessStatus: any,
  availableSyncList: any
};

type State = {
  syncType: any,
  percentage: number,
  triggerSyncFlag: boolean,
  syncProcessDot: string,
  isFirstReuqestGetStatusFlow: boolean
};

type ComposedProps = Props & WithI18nProps;

class ManualSync extends Component<ComposedProps, State> {
  timeoutGetSyncProcessStatus = undefined;
  intervalProgressDot = undefined;

  constructor(props) {
    super(props);
    this.state = {
      syncType: props.syncTypeOptions[0],
      percentage: 0,
      triggerSyncFlag: false,
      syncProcessDot: '',
      active: false,
      isFirstReuqestGetStatusFlow: false,
      isUpdateFromSchedule: false
    };
  }

  // Handle change sync type
  handleSelectSyncType = syncType => {
    this.setState({ syncType });

    // Call to get new process status
    clearTimeout(this.timeoutGetSyncProcessStatus);
  };

  handleStartManualSync = () => {
    const { syncType } = this.state;
    const syncTypeValue = _.get(syncType, 'value');
    if ([SYNC_TYPE_FLOW_MANUAL_SYNC, SYNC_TYPE_FLOW_B2B].includes(syncTypeValue)) {
      this.triggerRunSync();
    } else {
      this.handleClickOtherSync(syncTypeValue);
    }
  };

  // Handle click button sync with syncType !== FlowManualSync
  handleClickOtherSync = syncTypeValue => {
    switch (syncTypeValue) {
      case SYNC_TYPE_FLOW_BEST_SELLING: {
        // handle best selling sync
        const { collectionBoostAllExists } = this.props;
        if (collectionBoostAllExists) {
          this.triggerRunSync();
        } else {
          this.handleChangeModal('activeCollectionNotFoundModal');
        }
        return;
      }
      default: {
        // other sync
        this.handleChangeModal('activeConfirmSyncModal');
      }
    }
  };

  // Handle manual sync button
  triggerRunSync = async () => {
    const { syncType } = this.state;
    const syncTypeValue = _.get(syncType, 'value');
    const { triggerSync, getSyncProcessStatus, trackEvent, onShowProgress } = this.props;

    if (syncTypeValue === SYNC_TYPE_FLOW_MANUAL_SYNC) {
      this.setState({ isFirstReuqestGetStatusFlow: true });
    }

    // Disable button
    this.setState({ triggerSyncFlag: true, activeConfirmSyncModal: false });
    onShowProgress();
    clearTimeout(this.timeoutGetSyncProcessStatus);

    await triggerSync(syncTypeValue);
    await getSyncProcessStatus(syncTypeValue);
    trackEvent('csatFeedback', 'syncRecentUpdate', 'csatFeedback_syncRecentUpdate');

    trackEvent('activeCampaign', 'sync_recent_update', 'activeCampaign_sync_recent_update');
  };

  handleChangeModal = stateField => {
    const value = this.state[stateField] || false;
    this.setState({ [stateField]: !value });
  };

  openLink = (id: string) => {
    const newUrl = URL[id];
    window.open(newUrl, '_blank');
  };

  toggleModal = fieldName => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };

  /** Subscribe addon */
  handleCloseStatus = () => {
    this.props.getAddonStatus(SYNC_FREQUENCY_NAME);
  };

  componentDidMount() {}

  componentWillReceiveProps(nextProps) {
    const { syncTypeOptions, valueSelect } = nextProps;
    const nextStatusFlow = _.get(nextProps, 'flow.status', '');
    const currentStatusFlow = _.get(this.props, 'flow.status', '');
    if (currentStatusFlow === 'done' && nextStatusFlow === 'running')
      this.setState({ isFirstReuqestGetStatusFlow: false });
    if (
      nextStatusFlow !== currentStatusFlow &&
      (nextStatusFlow === 'error' || nextStatusFlow === 'done')
    ) {
      this.setState({
        triggerSyncFlag: false
      });
    }
    if (this.props.syncTypeOptions !== syncTypeOptions)
      this.setState({
        syncType: syncTypeOptions[0]
      });

    if (
      _.get(valueSelect, 'value') !== _.get(this.state.valueSelect, 'value') &&
      !this.state.isUpdateFromSchedule
    ) {
      this.setState({ syncType: valueSelect, isUpdateFromSchedule: true });
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timeoutGetSyncProcessStatus);
    clearInterval(this.intervalProgressDot);
  }

  openChatBox = () => {
    showChatbox();
  };

  render() {
    const {
      i18n,
      tenantId,
      accessScopes,
      triggerSyncLoading,
      onOpenScheduleSync,
      syncTypeOptions,
      emailSupport,
      flow,
      scheduleSyncInfo,
      enabledSubscriptionV4,
      syncProcessLoading,
      getSyncScheduleLoading,
      renderNextSyncScheduleDate
    } = this.props;

    const { status, flowName } = flow;
    const {
      syncType,
      triggerSyncFlag,
      activeConfirmSyncModal,
      activeCollectionNotFoundModal,
      isFirstReuqestGetStatusFlow
    } = this.state;

    let syncContent = getSyncContent(syncType?.value, i18n, enabledSubscriptionV4);
    let valueSelect = syncType;

    if (status === 'running') {
      syncContent = getSyncContent(flowName || syncType?.value, i18n, enabledSubscriptionV4);
      valueSelect = syncTypeOptions.find(o => {
        return o.value === (flowName || syncType?.value);
      });
    }

    let isDisabled =
      triggerSyncFlag ||
      triggerSyncLoading ||
      status === 'running' ||
      (syncProcessLoading && status === 'running');
    if (status === 'error') {
      isDisabled = false;
    }

    const scheduleDate = scheduleSyncInfo
      ? renderNextSyncScheduleDate(scheduleSyncInfo.scheduleAt || 0)
      : '';

    const needsCustomerPermission = !accessScopes.read_customers;
    const isB2BSync = syncType?.value === 'FlowB2B';

    return (
      <Card sectioned title={i18n.translate('AdminHTML.Sync.ManualSync.title')}>
        <div className="content">
          {getSyncScheduleLoading ? (
            <div>
              <SkeletonBodyText lines={2} />
              <SkeletonBodyText lines={2} />
              <SkeletonBodyText lines={2} />
              <div style={{ marginBottom: '20px' }} />
            </div>
          ) : (
            <div>
              <Select
                options={syncTypeOptions}
                onChange={this.handleSelectSyncType}
                value={valueSelect}
              />
              <div className="description">{syncContent.description}</div>
            </div>
          )}

          {getSyncScheduleLoading ? (
            <SkeletonBodyText lines={2} />
          ) : (
            <div className="action">
              {isB2BSync ? (
                // B2B Sync Type
                needsCustomerPermission ? (
                  <Button url={buildUpdateAppPermissionURL(tenantId)} external>
                    {i18n.translate('AdminHTML.Sync.ManualSync.requestCustomerPermission')}
                  </Button>
                ) : (
                  <Button
                    primary
                    onClick={this.handleStartManualSync}
                    loading={triggerSyncLoading || isFirstReuqestGetStatusFlow}
                    disabled={isDisabled}>
                    {i18n.translate('AdminHTML.Sync.ManualSync.startManualSyncBtn')}
                  </Button>
                )
              ) : (
                // Non-B2B Sync Type
                <>
                  <Button
                    primary
                    onClick={this.handleStartManualSync}
                    loading={triggerSyncLoading || isFirstReuqestGetStatusFlow}
                    disabled={isDisabled}>
                    {i18n.translate('AdminHTML.Sync.ManualSync.startManualSyncBtn')}
                  </Button>
                  {scheduleDate === '' && (
                    <Button onClick={() => onOpenScheduleSync(true, valueSelect)}>
                      {i18n.translate('AdminHTML.Sync.ManualSync.scheduleSyncBtn')}
                    </Button>
                  )}
                </>
              )}
            </div>
          )}

          {status === 'error' && (
            <p style={{ marginTop: '8px', color: '#202223' }}>
              {i18n.translate('AdminHTML.Sync.Sync.renderSpecialSyncProgress_Error_Paragraph_New', {
                nt_process_type: syncContent.name?.toLowerCase(),
                t: (
                  <span
                    style={{ textDecoration: 'underline', cursor: 'pointer' }}
                    onClick={this.openChatBox}>
                    {i18n.translate(
                      'AdminHTML.Sync.Sync.renderSpecialSyncProgress_Error_Paragraph_New_t'
                    )}
                  </span>
                )
              })}
            </p>
          )}
        </div>
        <Modal
          large
          open={activeConfirmSyncModal}
          onClose={() => this.handleChangeModal('activeConfirmSyncModal')}
          title={
            syncContent
              ? i18n.translate('AdminHTML.Sync.Sync.modal1_Title_WithSubject', {
                  nt_subject: syncContent.textTitle
                })
              : i18n.translate('AdminHTML.Sync.Sync.modal1_Title_WithoutSubject')
          }
          primaryAction={{
            content: i18n.translate('AdminHTML.Sync.Sync.modal1_PrimaryAction'),
            onAction: this.triggerRunSync
          }}
          secondaryActions={[
            {
              content: i18n.translate('AdminHTML.Common.Action.cancel'),
              onAction: () => this.handleChangeModal('activeConfirmSyncModal')
            }
          ]}>
          <Modal.Section>
            <TextContainer>
              <p>
                {syncContent
                  ? i18n.translate('AdminHTML.Sync.Sync.modal1_Paragraph_WithSubject', {
                      t1: (
                        <TextStyle variation="strong">
                          {i18n.translate('AdminHTML.Sync.Sync.modal1_Paragraph_WithSubject_t1')}
                        </TextStyle>
                      ),
                      t2: (
                        <TextStyle variation="strong">
                          {i18n.translate('AdminHTML.Sync.Sync.modal1_Paragraph_WithSubject_t2')}
                        </TextStyle>
                      ),
                      nt_subject: syncContent.textContent,
                      t3: (
                        <TextStyle variation="strong">
                          {i18n.translate('AdminHTML.Sync.Sync.modal1_Paragraph_WithSubject_t3')}
                        </TextStyle>
                      )
                    })
                  : i18n.translate('AdminHTML.Sync.Sync.modal1_Paragraph_WithoutSubject', {
                      t1: (
                        <TextStyle variation="strong">
                          {i18n.translate('AdminHTML.Sync.Sync.modal1_Paragraph_WithoutSubject_t1')}
                        </TextStyle>
                      ),
                      t2: (
                        <TextStyle variation="strong">
                          {i18n.translate('AdminHTML.Sync.Sync.modal1_Paragraph_WithoutSubject_t2')}
                        </TextStyle>
                      ),
                      t3: (
                        <TextStyle variation="strong">
                          {i18n.translate('AdminHTML.Sync.Sync.modal1_Paragraph_WithoutSubject_t3')}
                        </TextStyle>
                      )
                    })}
              </p>
            </TextContainer>
          </Modal.Section>
        </Modal>
        <Modal
          large
          open={activeCollectionNotFoundModal}
          onClose={() => this.handleChangeModal('activeCollectionNotFoundModal')}
          title={
            <TextStyle>
              {i18n.translate('AdminHTML.Sync.Sync.modal2_Title', {
                t1: (
                  <TextStyle variation="strong">
                    {i18n.translate('AdminHTML.Sync.Sync.modal2_Title_t1')}
                  </TextStyle>
                )
              })}
            </TextStyle>
          }
          primaryAction={{
            content: i18n.translate('AdminHTML.Sync.Sync.modal2_PrimaryAction'),
            onAction: () => this.openLink('SyncDocBoostAllCollection')
          }}
          secondaryActions={[
            {
              content: i18n.translate('AdminHTML.Common.Action.cancel'),
              onAction: () => this.handleChangeModal('activeCollectionNotFoundModal')
            }
          ]}>
          <Modal.Section>
            <TextContainer>
              <p>
                {i18n.translate('AdminHTML.Sync.Sync.modal2_Paragraph1', {
                  t1: (
                    <Link external url={URL['SyncDocBoostAllCollection']}>
                      {i18n.translate('AdminHTML.Sync.Sync.modal2_Paragraph1_t1')}
                    </Link>
                  )
                })}
              </p>
              <p>
                {i18n.translate('AdminHTML.Sync.Sync.modal2_Paragraph2', {
                  nt_email: <TextStyle variation="strong">{emailSupport}</TextStyle>
                })}
              </p>
            </TextContainer>
          </Modal.Section>
        </Modal>
      </Card>
    );
  }
}

const mapStateToProps = (state, props) => {
  const flow = _.get(state.sync, 'flow', {});
  const scheduleSyncInfo = _.get(state.sync, 'scheduleSyncInfo', {});
  const syncSchedule = _.get(state.sync, 'syncSchedule', {});
  const accessScopes = _.get(state.oauth, 'tenantApp.accessScopes', {});

  return {
    tenantApp: _.get(state, 'oauth.tenantApp', {}),
    accessScopes,
    tenantId: _.get(state.oauth.tenantApp, 'tenantId', ''),
    error: _.get(state.sync, 'error', undefined),
    lastSyncedDoneAt: _.get(state.sync, 'lastSyncedDoneAt', '0'),
    triggerSyncLoading: _.get(state.sync, 'triggerSyncLoading', false),
    syncProcessLoading: _.get(state.sync, 'syncProcessLoading', false),
    flow,
    enabledSubscriptionV4: _.get(
      state.oauth.tenantApp,
      'settings.subscriptionV4.settings.enable',
      false
    ),
    syncProcess: _.get(state.sync, 'syncProcess', {}),
    availableSyncList: _.get(state.sync, 'availableSyncList', []),
    collectionBoostAllExists: _.get(state.sync, 'collectionBoostAllExists', false),
    installSyncStatus: _.get(state.install, 'syncStatus'),
    installSyncProgress: _.get(state.install, 'syncProgress'),
    installSyncStartedAt: _.get(state.install, 'syncStartedAt'),
    installSyncRemainingTime: _.get(state.install, 'syncRemainingTime', 0),
    emailSupport: _.get(state.oauth, 'emailSupport'),
    syncSchedule,
    scheduleSyncInfo,
    getSyncScheduleLoading: _.get(state.sync, 'getSyncScheduleLoading', false)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  // Get sync process status
  getSyncProcessStatus: (flowName = '') => dispatch(syncActions.getSyncProcessStatus(flowName)),
  // Get sync status
  getSyncStatus: tenantId => dispatch(syncActions.getSyncStatus(tenantId)),
  resetSyncStatus: () => dispatch(syncActions.resetSyncStatus()),
  triggerSync: flowName => dispatch(syncActions.triggerSync(flowName, ownProps.i18n)),
  trackEvent: (eventSource, eventName, label) =>
    dispatch(checklistActions.trackEvent(eventSource, eventName, label))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(ManualSync));
